"use client";

import Image from "next/image";
import { Flame, Users, Brain, HeartOff, Briefcase } from "lucide-react";

interface SubFeature {
  title: string;
  description: string;
  icon: React.ReactNode;
}

const subFeatures: SubFeature[] = [
  {
    title: "Anger Management",
    description: "Control emotional triggers at home or in the workplace.",
    icon: <Flame className="h-5 w-5 text-white" />,
  },
  {
    title: "Relationships",
    description:
      "Couples therapy for communication breakdowns, breakups, or family conflict.",
    icon: <Users className="h-5 w-5 text-white" />,
  },
  {
    title: "Depression and anxiety",
    description:
      "Break negative cycles while building calming coping mechanisms.",
    icon: <Brain className="h-5 w-5 text-white" />,
  },
  {
    title: "Grief and loss",
    description: "Compassionate therapy for trauma or the loss of a loved one.",
    icon: <HeartOff className="h-5 w-5 text-white" />,
  },
  {
    title: "Career counselling",
    description:
      "Professional guidance for workplace burnout, bullying, or feeling stuck.",
    icon: <Briefcase className="h-5 w-5 text-white" />,
  },
];

const CounsellingAndLifeCoaching = () => {
  return (
    <section className="container" aria-labelledby="section-heading">
      <div className="flex flex-col gap-8 sm:gap-10 md:gap-12">
        {/* Header Section */}
        <div className="flex max-w-[950px] flex-col gap-3 sm:gap-4">
          <div className="inline-flex">
            <span className="font-crimsonText text-[20px] font-bold leading-none text-[#2857A7] sm:text-[24px] md:text-[28px]">
              Counselling, Life Coaching & Allied Health
            </span>
          </div>
          <h2 id="section-heading" className="heading text-[#02386D]">
            Pathways to Feeling Better, Living Clearer, and Moving Forward
          </h2>
        </div>

        {/* Main Grid Layout */}
        <div className="grid w-full grid-cols-1 gap-4 sm:gap-5 lg:grid-cols-2">
          {/* Column 1: Stack of Card 1 and Card 2 */}
          <div className="flex h-full w-full flex-col gap-4 sm:gap-5">
            {/* Card 1 */}
            <article
              className="relative flex min-h-[320px] w-full flex-1 flex-col overflow-hidden rounded-[10px] bg-[#02376D] shadow-lg sm:min-h-[380px] md:min-h-[420px] lg:min-h-[513px]"
              aria-label="Allied health topic: Support to Move Forward, Physically and Mentally"
            >
              {/* Image */}
              <Image
                src="/images/v3/about/c-l-1.png"
                alt="Client meeting with an allied health professional to discuss a care plan"
                fill
                className="object-cover"
                sizes="(max-width: 1024px) 100vw, 750px"
                priority
              />

              {/* Gradient overlay for readability */}
              <div className="pointer-events-none absolute inset-0 z-10 bg-gradient-to-t from-[#02376D] from-[10%] via-[#02376D]/45 to-transparent" />

              {/* Content */}
              <div className="absolute bottom-0 left-0 right-0 z-20 flex flex-col justify-end gap-2 p-4 sm:gap-3 sm:p-6 md:gap-4 md:p-8">
                <h3 className="text-left font-crimsonText text-[24px] font-bold leading-tight text-white sm:text-[28px] md:text-[32px]">
                  Support to Move Forward, Physically and Mentally
                </h3>
                <p className="text-left font-onest text-[14px] font-light leading-relaxed text-white/80 sm:text-[16px] md:text-[18px]">
                  Living with an injury, disability, or ongoing health
                  challenge can make even simple daily tasks feel exhausting.
                  Our occupational therapists and psychologists work alongside
                  you to build practical skills, restore function, and support
                  your emotional wellbeing — one step at a time.
                </p>
              </div>
            </article>

            {/* Card 2 */}
            <article
              className="relative flex min-h-[320px] w-full flex-1 flex-col overflow-hidden rounded-[10px] bg-[#02376D] shadow-lg sm:min-h-[380px] md:min-h-[420px] lg:min-h-[513px]"
              aria-label="Life coaching topic: From a Static Present to an Empowered Future"
            >
              {/* Image */}
              <Image
                src="/images/v3/about/c-l-2.png"
                alt="Man smiling in office having photo taken"
                fill
                className="object-cover"
                sizes="(max-width: 1024px) 100vw, 750px"
              />

              {/* Gradient overlay for readability */}
              <div className="pointer-events-none absolute inset-0 z-10 bg-gradient-to-t from-[#02376D] from-[10%] via-[#02376D]/45 to-transparent" />

              {/* Content */}
              <div className="absolute bottom-0 left-0 right-0 z-20 flex flex-col justify-end gap-2 p-4 sm:gap-3 sm:p-6 md:gap-4 md:p-8">
                <h3 className="text-left font-crimsonText text-[24px] font-bold leading-tight text-white sm:text-[28px] md:text-[32px]">
                  From a Static Present to an Empowered Future
                </h3>
                <p className="text-left font-onest text-[14px] font-light leading-relaxed text-white/80 sm:text-[16px] md:text-[18px]">
                  If you feel stuck in your current routine or held back by
                  mental blocks, proactive life coaching can help. Our
                  goal-driven strategies provide the clarity, accountability,
                  and practical tools you need to break old patterns and step
                  into an empowered future.
                </p>
              </div>
            </article>
          </div>

          {/* Column 2: Card 3 (Tall Card) */}
          <div className="h-full w-full">
            <article
              className="flex h-full min-h-[700px] w-full flex-col overflow-hidden rounded-[10px] bg-[#02376D] shadow-lg sm:min-h-[800px] md:min-h-[900px] lg:min-h-[1046px]"
              aria-label="Counselling topic: From a Troubled Past to a Positive Future"
            >
              {/* Image */}
              <div className="relative h-[280px] w-full shrink-0 sm:h-[350px] md:h-[420px] lg:h-[542px]">
                <Image
                  src="/images/v3/about/c-l-3.png"
                  alt="Asian depressed man sitting and sobbing alone"
                  fill
                  className="object-cover"
                  sizes="(max-width: 1024px) 100vw, 750px"
                  priority
                />
                {/* Gradient blending image into card background */}
                <div
                  className="absolute inset-x-0 bottom-0 z-10 h-[20%]"
                  style={{
                    background:
                      "linear-gradient(to bottom, transparent 0%, #02376D 100%)",
                  }}
                />
              </div>

              {/* Content */}
              <div className="z-20 flex flex-1 flex-col justify-between gap-6 p-4 sm:gap-8 sm:p-6 md:p-8">
                {/* Header text */}
                <div className="flex flex-col gap-3 sm:gap-4 md:gap-5">
                  <h3 className="text-left font-crimsonText text-[24px] font-bold leading-tight text-white sm:text-[28px] md:text-[32px]">
                    From a Troubled Past to a Positive Future
                  </h3>
                  <p className="text-left font-onest text-[14px] font-light leading-relaxed text-white/80 sm:text-[16px] md:text-[18px]">
                    Counselling gives you a calm and confidential space to talk
                    through the things that feel heavy. Whether you are carrying
                    stress, grief, anxiety, relationship struggles, anger, or
                    emotional pain, you do not have to process everything alone.
                    Connect with an experienced therapist to find your way
                    forward.
                  </p>
                </div>

                {/* Sub-features list */}
                <div className="flex flex-col gap-3 border-t border-white/10 pt-4 sm:gap-4 sm:pt-6 md:gap-5 md:pt-8">
                  {subFeatures.map((feat, i) => (
                    <div
                      key={i}
                      className="flex w-full items-start gap-3 sm:items-center sm:gap-4"
                    >
                      {/* Icon */}
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/10 sm:h-10 sm:w-10">
                        {feat.icon}
                      </div>

                      {/* Text details inline */}
                      <div className="flex flex-col gap-0.5 text-left font-onest text-[14px] leading-snug sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-2.5 sm:text-[16px] sm:leading-none md:text-[18px]">
                        <span className="font-semibold text-white">
                          {feat.title}
                        </span>
                        <span className="font-normal text-white/50">
                          <span className="hidden sm:inline">&mdash; </span>
                          {feat.description}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </article>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CounsellingAndLifeCoaching;
